/**
 * OBJECTS-section annotative writers (PR-5 of the annotation-scale parity
 * plan). Inverse of `objectsAnnotativeParser.ts` — every parser has a
 * matching writer here, and the round-trip
 * `parser → writer → parser` is exercised by `objectsAnnotativeWriter.test.ts`.
 *
 * Reference: AutoCAD ObjectARX 2018 reference, "AcDbObjectContextData"
 * class hierarchy. Group code orderings mirror the order the parser
 * reads them so the file diff stays readable; for round-trip the order
 * of unrelated codes is irrelevant — only their values matter.
 *
 * ## On-disk shape produced by these writers
 *
 * `AcDbScale` (catalogue entry — emitted by `serializeAcDbScale`):
 * ```
 * 0   SCALE
 * 5   <handle>
 * 330 <ownerHandle>
 * 100 AcDbScale
 * 70  <flags>
 * 300 <name>
 * 140 <paperUnits>
 * 141 <drawingUnits>
 * 290 <isUnitScale>
 * ```
 *
 * `AcDb*ObjectContextData` (per-scale record — emitted by
 * `serializeAnnotScaleContextRecord`). AutoCAD's actual emission
 * (observed in АБИ1.dxf, the QA fixture) carries ONLY the base +
 * intermediate markers. The concrete `AcDb<Kind>ObjectContextData` 100
 * marker is OMITTED — concrete kind is encoded only in group 0 via the
 * registered DXF class name. We reproduce that exact shape so the
 * round-trip file diff is byte-clean against AutoCAD-authored input:
 * ```
 * 0   <SUBCLASS_TO_TYPE[subclass]>      e.g. ACDB_TEXTOBJECTCONTEXTDATA_CLASS
 * 5   <handle>
 * 330 <ownerHandle>
 * 100 AcDbObjectContextData
 * 70  <flags>
 * 100 AcDbAnnotScaleObjectContextData    ← intermediate base
 * 340 <scaleHandle>                      ← FK into ACAD_SCALELIST
 * <subclass-specific groups>
 * <rawGroups (round-trip)>
 * ```
 *
 * `ACAD_SCALELIST` DICTIONARY (emitted by `serializeAcDbScaleListDict`):
 * ```
 * 0   DICTIONARY
 * 5   <handle>
 * 100 AcDbDictionary
 * 280 0
 * 281 1
 * 3   <name>
 * 350 <handle>
 * (repeats per scale entry)
 * ```
 *
 * `AcadAnnotativeDecomposition` XData on a BLOCK_RECORD (emitted by
 * `serializeAcadAnnotativeDecomposition`):
 * ```
 * 1001 AcadAnnotativeDecomposition
 * 1000 <scaleName>
 * 1005 <anonymousBlockHandle>
 * (repeats per scale entry)
 * ```
 *
 * ## Why these live in their own module
 *
 * `writer.ts` is already 1817 LoC and the addition of ten subclass
 * writers + AcDbScale + ACAD_SCALELIST dict serializer would push it
 * past the 2000-line hard limit per CLAUDE.md priority #2 + file-size
 * rules. This module mirrors the parser sibling at
 * `objectsAnnotativeParser.ts` so the two stay easy to keep in sync.
 *
 * ## Round-trip preservation
 *
 * Per CLAUDE.md priority #1 ("preserve unknowns") every per-subclass
 * writer accepts an optional `rawGroups: DxfGroup[]` on the context
 * value and emits those groups VERBATIM after its own well-known
 * group codes. The combined parser-writer pair is therefore lossless
 * for any vendor or future-AutoCAD group code that happens to live
 * inside a context-data body.
 */

import type { DxfGroup } from "../types";
import { pushGroup } from "./groupCodec";
import type {
  AcDbScale,
  AcadScaleListDictEntry,
  AnnotScaleAttributeContext,
  AnnotScaleBlockrefContext,
  AnnotScaleContext,
  AnnotScaleDimensionContext,
  AnnotScaleDimensionSubtype,
  AnnotScaleHatchContext,
  AnnotScaleLeaderContext,
  AnnotScaleMleaderContext,
  AnnotScaleMtextAttributeContext,
  AnnotScaleMtextContext,
  AnnotScaleTextContext,
  AnnotScaleToleranceContext,
} from "./objectsAnnotativeParser";

// ── Helpers ────────────────────────────────────────────────────────────
//
// The DXF group-value codec (pushGroup / formatGroupNumber) lives in the
// shared `groupCodec` leaf module — see the import above. Formerly local
// copies; consolidated to one truth across the objects/ parsers + writers.

// ── Subclass markers ──────────────────────────────────────────────────

const SUBCLASS_TEXT = "AcDbTextObjectContextData";
const SUBCLASS_MTEXT = "AcDbMTextObjectContextData";
const SUBCLASS_MTEXT_ATTR = "AcDbMTextAttributeObjectContextData";
const SUBCLASS_BLKREF = "AcDbBlkRefObjectContextData";
const SUBCLASS_HATCH = "AcDbHatchScaleContextData";
const SUBCLASS_DIM = "AcDbDimensionObjectContextData";
const SUBCLASS_DIM_ALIGNED = "AcDbAlignedDimensionObjectContextData";
const SUBCLASS_DIM_LINEAR = "AcDbRotatedDimensionObjectContextData";
const SUBCLASS_DIM_ANGULAR_3PT = "AcDb3PointAngularDimensionObjectContextData";
const SUBCLASS_DIM_ANGULAR_2L = "AcDb2LineAngularDimensionObjectContextData";
const SUBCLASS_DIM_DIAM = "AcDbDiametricDimensionObjectContextData";
const SUBCLASS_DIM_RAD = "AcDbRadialDimensionObjectContextData";
const SUBCLASS_DIM_ORD = "AcDbOrdinateDimensionObjectContextData";
const SUBCLASS_MLEADER = "AcDbMLeaderObjectContextData";
const SUBCLASS_LEADER = "AcDbLeaderObjectContextData";
const SUBCLASS_FCF = "AcDbFcfObjectContextData";
const SUBCLASS_BASE = "AcDbObjectContextData";
const SUBCLASS_ANNOT_BASE = "AcDbAnnotScaleObjectContextData";

/**
 * Inverse of `TYPE_TO_SUBCLASS` from the parser. Maps the concrete
 * 100-marker subclass name back to the registered DXF class name that
 * AutoCAD writes in group 0. We pick the canonical (non-deprecated)
 * spelling per Autodesk's CLASSES section conventions — this is what
 * AutoCAD-authored files actually emit.
 *
 * Used when serialising a context-data record so that round-trip
 * `parser(input) → writer(parsed) → parser(written) → deepEqual` holds
 * for AutoCAD-authored fixtures (e.g. АБИ1.dxf).
 */
const SUBCLASS_TO_TYPE: Readonly<Record<string, string>> = {
  [SUBCLASS_TEXT]: "ACDB_TEXTOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_MTEXT]: "ACDB_MTEXTOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_MTEXT_ATTR]: "ACDB_MTEXTATTRIBUTEOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_BLKREF]: "ACDB_BLKREFOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_HATCH]: "ACDB_HATCHSCALECONTEXTDATA_CLASS",
  [SUBCLASS_DIM]: "ACDB_DIMENSIONOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_DIM_ALIGNED]: "ACDB_ALDIMOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_DIM_LINEAR]: "ACDB_RDIMOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_DIM_ANGULAR_3PT]: "ACDB_ANG3PTDIMOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_DIM_ANGULAR_2L]: "ACDB_ANG2LDIMOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_DIM_DIAM]: "ACDB_DMDIMOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_DIM_RAD]: "ACDB_RADIMOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_DIM_ORD]: "ACDB_ORDDIMOBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_MLEADER]: "ACDB_MLEADEROBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_LEADER]: "ACDB_LEADEROBJECTCONTEXTDATA_CLASS",
  [SUBCLASS_FCF]: "ACDB_FCFOBJECTCONTEXTDATA_CLASS",
};

/** Map from dimension subtype enum back to the concrete subclass marker. */
const DIMENSION_SUBTYPE_TO_SUBCLASS: Readonly<
  Record<AnnotScaleDimensionSubtype, string>
> = {
  aligned: SUBCLASS_DIM_ALIGNED,
  linear: SUBCLASS_DIM_LINEAR,
  // The parser folds both 3-point and 2-line angular into "angular" — we
  // emit the more common 3-point variant on round-trip when the original
  // subclass was lost. Real-world records preserve the concrete subclass
  // through the parsed.subclass field on `ParsedAnnotativeRecord`, so the
  // writer of the OBJECTS-section catch-all uses that field directly when
  // available (see `serializeAnnotScaleContextRecord`).
  angular: SUBCLASS_DIM_ANGULAR_3PT,
  diametric: SUBCLASS_DIM_DIAM,
  radial: SUBCLASS_DIM_RAD,
  ordinate: SUBCLASS_DIM_ORD,
};

// ── serializeAcDbScale ─────────────────────────────────────────────────

/**
 * Serialize a single `AcDbScale` catalogue entry as DXF groups. Inverse
 * of `parseAcDbScale`. Emits the canonical AutoCAD shape:
 *
 * ```
 * 0   SCALE
 * 5   <handle>
 * 330 <ownerHandle>            ← if present
 * 100 AcDbScale
 * 70  <flags>
 * 300 <name>
 * 140 <paperUnits>
 * 141 <drawingUnits>
 * 290 <isUnitScale 0|1>
 * ```
 *
 * The `isHidden` boolean is not emitted separately — it is encoded in
 * bit 0 of `flags` per the AutoCAD ObjectARX reference, and parser sets
 * `flags` accordingly when constructing the catalogue entry.
 *
 * `scaleFactor` is also not emitted — it is the derived
 * `paperUnits / drawingUnits` ratio reconstructed by the parser on the
 * round-trip.
 */
export function serializeAcDbScale(scale: AcDbScale): DxfGroup[] {
  const out: DxfGroup[] = [];
  pushGroup(out, 0, "SCALE");
  if (scale.handle) pushGroup(out, 5, scale.handle);
  if (scale.ownerHandle) pushGroup(out, 330, scale.ownerHandle);
  pushGroup(out, 100, "AcDbScale");
  pushGroup(out, 70, scale.flags);
  pushGroup(out, 300, scale.name);
  pushGroup(out, 140, scale.paperUnits);
  pushGroup(out, 141, scale.drawingUnits);
  pushGroup(out, 290, scale.isUnitScale ? 1 : 0);
  return out;
}

// ── serializeAcDbScaleListDict ─────────────────────────────────────────

/**
 * Serialize the `ACAD_SCALELIST` DICTIONARY into DXF groups. Inverse of
 * `parseAcDbScaleListDict`. Mirrors `serializeDictionaryEntry` from
 * `writer.ts` but specialised for the ACAD_SCALELIST shape — fixed
 * `hardOwned: false` (group 280 = 0) and `cloning: 1` (group 281),
 * which is what AutoCAD writes for this dictionary.
 *
 * Pointers to AcDbScale records are emitted as soft pointers (group
 * 350) to match what AutoCAD-authored files contain; the parser
 * accepts either 350 or 360, so `name → handle` round-trip is preserved
 * through the soft-pointer path.
 *
 * Emits the `102 {ACAD_REACTORS … 102 }` reactor block (before the owner) and
 * the `330` owner handle (before `100 AcDbDictionary`) to match the canonical
 * DICTIONARY ordering AutoCAD/R2018 writes (mirrors `serializeDictionaryEntry`
 * in writer.ts). Without the `330` owner ezdxf `audit()` flags the SCALELIST
 * DICTIONARY as orphaned (`INVALID_OWNER_HANDLE` fix). Both are conditional so a
 * synthesized dict with no owner/reactors stays minimal.
 */
export function serializeAcDbScaleListDict(
  dict: AcadScaleListDictEntry,
): DxfGroup[] {
  const out: DxfGroup[] = [];
  pushGroup(out, 0, "DICTIONARY");
  if (dict.handle) pushGroup(out, 5, dict.handle);
  // Persistent-reactor block (mirror BLOCK_RECORD / serializeDictionaryEntry):
  // emitted BEFORE the 330 owner to match AutoCAD's canonical 102-block ordering.
  if (dict.reactorHandles && dict.reactorHandles.length > 0) {
    pushGroup(out, 102, "{ACAD_REACTORS");
    for (const h of dict.reactorHandles) pushGroup(out, 330, h);
    pushGroup(out, 102, "}");
  }
  if (dict.ownerHandle) pushGroup(out, 330, dict.ownerHandle);
  pushGroup(out, 100, "AcDbDictionary");
  pushGroup(out, 280, 0);
  pushGroup(out, 281, 1);
  for (const ref of dict.scales) {
    pushGroup(out, 3, ref.name);
    pushGroup(out, 350, ref.handle);
  }
  return out;
}

// ── Per-subclass body writers (inverse of parsers in the sibling) ──────

/**
 * AcDbTextObjectContextData body writer. Emits insertion (10/20),
 * optional alignment (11/21), rotation (50). Inverse of
 * `parseTextContext`.
 */
function writeTextBody(ctx: AnnotScaleTextContext, out: DxfGroup[]): void {
  pushGroup(out, 10, ctx.insertion.x);
  pushGroup(out, 20, ctx.insertion.y);
  if (ctx.alignment) {
    pushGroup(out, 11, ctx.alignment.x);
    pushGroup(out, 21, ctx.alignment.y);
  }
  pushGroup(out, 50, ctx.rotation);
}

/**
 * AcDbMTextObjectContextData body writer. Emits insertion (10/20),
 * rotation (50), optional reference width (41), optional attachment
 * (71). Inverse of `parseMtextContext`.
 */
function writeMtextBody(ctx: AnnotScaleMtextContext, out: DxfGroup[]): void {
  pushGroup(out, 10, ctx.insertion.x);
  pushGroup(out, 20, ctx.insertion.y);
  pushGroup(out, 50, ctx.rotation);
  if (ctx.width !== undefined) pushGroup(out, 41, ctx.width);
  if (ctx.attachment !== undefined) pushGroup(out, 71, ctx.attachment);
}

/**
 * AcDbMTextAttributeObjectContextData body writer. Emits insertion
 * (10/20), rotation (50), tag (2). Inverse of
 * `parseMtextAttributeContext`.
 */
function writeMtextAttributeBody(
  ctx: AnnotScaleMtextAttributeContext,
  out: DxfGroup[],
): void {
  pushGroup(out, 10, ctx.insertion.x);
  pushGroup(out, 20, ctx.insertion.y);
  pushGroup(out, 50, ctx.rotation);
  pushGroup(out, 2, ctx.attribTag);
}

/**
 * AcDbBlkRefObjectContextData body writer (attribute discriminant —
 * with tag). Emits insertion (10/20), rotation (50), tag (2). The
 * scaleX/scaleY group codes (41/42) are NOT emitted for attributes
 * because they are not present in the parsed shape (parser drops them
 * when discriminating to "attribute"). Inverse of the attribute branch
 * of `parseBlkRefContext`.
 */
function writeAttributeBody(
  ctx: AnnotScaleAttributeContext,
  out: DxfGroup[],
): void {
  pushGroup(out, 10, ctx.insertion.x);
  pushGroup(out, 20, ctx.insertion.y);
  pushGroup(out, 50, ctx.rotation);
  pushGroup(out, 2, ctx.attribTag);
}

/**
 * AcDbBlkRefObjectContextData body writer (blockref discriminant —
 * without tag). Emits insertion (10/20), scaleX (41), scaleY (42),
 * rotation (50). Inverse of the blockref branch of `parseBlkRefContext`.
 */
function writeBlockrefBody(
  ctx: AnnotScaleBlockrefContext,
  out: DxfGroup[],
): void {
  pushGroup(out, 10, ctx.insertion.x);
  pushGroup(out, 20, ctx.insertion.y);
  pushGroup(out, 41, ctx.scaleX);
  pushGroup(out, 42, ctx.scaleY);
  pushGroup(out, 50, ctx.rotation);
}

/**
 * AcDbHatchScaleContextData body writer. Emits patternScale (41),
 * patternAngle (53). Inverse of `parseHatchContext`.
 */
function writeHatchBody(ctx: AnnotScaleHatchContext, out: DxfGroup[]): void {
  pushGroup(out, 41, ctx.patternScale);
  pushGroup(out, 53, ctx.patternAngle);
}

/**
 * AcDb*DimensionObjectContextData body writer. Emits repeating 10/20
 * pairs for `definitionPoints[]` and an optional 11/21 pair for
 * `textPosition`. The dimension subtype itself is encoded in the
 * caller's 100 marker, not in the body. Inverse of
 * `parseDimensionContext`.
 */
function writeDimensionBody(
  ctx: AnnotScaleDimensionContext,
  out: DxfGroup[],
): void {
  for (const p of ctx.definitionPoints) {
    pushGroup(out, 10, p.x);
    pushGroup(out, 20, p.y);
  }
  if (ctx.textPosition) {
    pushGroup(out, 11, ctx.textPosition.x);
    pushGroup(out, 21, ctx.textPosition.y);
  }
}

/**
 * AcDbMLeaderObjectContextData body writer. Emits all optional MLEADER
 * scale-context fields independently — every field is conditional on
 * having been set on the parsed context. Inverse of `parseMleaderContext`.
 */
function writeMleaderBody(
  ctx: AnnotScaleMleaderContext,
  out: DxfGroup[],
): void {
  if (ctx.textHeight !== undefined) pushGroup(out, 40, ctx.textHeight);
  if (ctx.arrowheadSize !== undefined) pushGroup(out, 140, ctx.arrowheadSize);
  if (ctx.doglegLength !== undefined) pushGroup(out, 41, ctx.doglegLength);
  if (ctx.landingGap !== undefined) pushGroup(out, 145, ctx.landingGap);
  if (ctx.textRotation !== undefined) pushGroup(out, 42, ctx.textRotation);
  if (ctx.textColor !== undefined) pushGroup(out, 178, ctx.textColor);
  if (ctx.textWidth !== undefined) pushGroup(out, 43, ctx.textWidth);
}

/**
 * AcDbLeaderObjectContextData body writer. Emits the leader vertex
 * polyline as repeating 10/20 pairs. Inverse of `parseLeaderContext`.
 */
function writeLeaderBody(
  ctx: AnnotScaleLeaderContext,
  out: DxfGroup[],
): void {
  for (const v of ctx.vertices) {
    pushGroup(out, 10, v.x);
    pushGroup(out, 20, v.y);
  }
}

/**
 * AcDbFcfObjectContextData (Feature Control Frame / TOLERANCE) body
 * writer. Emits insertion (10/20) and rotation (50). Inverse of
 * `parseToleranceContext`.
 */
function writeToleranceBody(
  ctx: AnnotScaleToleranceContext,
  out: DxfGroup[],
): void {
  pushGroup(out, 10, ctx.insertion.x);
  pushGroup(out, 20, ctx.insertion.y);
  pushGroup(out, 50, ctx.rotation);
}

/**
 * Discriminated dispatch by `context.kind` to the matching body writer.
 * Pure (no side effects beyond `out.push(...)`) and exhaustive — adding
 * a new variant to `AnnotScaleContext` will surface a TS error here
 * until the writer is extended, providing the same compile-time gate as
 * the parser sibling has on its dispatcher.
 */
function writeContextBody(ctx: AnnotScaleContext, out: DxfGroup[]): void {
  switch (ctx.kind) {
    case "text":
      writeTextBody(ctx, out);
      break;
    case "mtext":
      writeMtextBody(ctx, out);
      break;
    case "mtext_attribute":
      writeMtextAttributeBody(ctx, out);
      break;
    case "attribute":
      writeAttributeBody(ctx, out);
      break;
    case "blockref":
      writeBlockrefBody(ctx, out);
      break;
    case "hatch":
      writeHatchBody(ctx, out);
      break;
    case "dimension":
      writeDimensionBody(ctx, out);
      break;
    case "mleader":
      writeMleaderBody(ctx, out);
      break;
    case "leader":
      writeLeaderBody(ctx, out);
      break;
    case "tolerance":
      writeToleranceBody(ctx, out);
      break;
    default: {
      // Exhaustiveness gate — this assignment fails at compile time if
      // a new kind is added to AnnotScaleContext without a matching
      // writer body above.
      const _exhaustive: never = ctx;
      void _exhaustive;
    }
  }
  // Round-trip preservation per CLAUDE.md priority #1 — append any
  // unrecognised body groups verbatim after the well-known codes.
  if (ctx.rawGroups) {
    for (const g of ctx.rawGroups) out.push(g);
  }
}

// ── serializeAnnotScaleContextRecord ───────────────────────────────────

/**
 * Resolve the concrete subclass marker for an annotScaleContexts record
 * given its parsed context. Prefers the explicit `subclass` field
 * (which the parser always populates from the original 100 marker or
 * the TYPE_TO_SUBCLASS fallback); falls back to a kind-based lookup
 * only when serialising a synthesised record with no recorded subclass.
 */
function resolveSubclass(
  recordSubclass: string | undefined,
  context: AnnotScaleContext,
): string {
  if (recordSubclass && recordSubclass.length > 0) return recordSubclass;
  switch (context.kind) {
    case "text":
      return SUBCLASS_TEXT;
    case "mtext":
      return SUBCLASS_MTEXT;
    case "mtext_attribute":
      return SUBCLASS_MTEXT_ATTR;
    case "attribute":
    case "blockref":
      return SUBCLASS_BLKREF;
    case "hatch":
      return SUBCLASS_HATCH;
    case "dimension":
      return DIMENSION_SUBTYPE_TO_SUBCLASS[context.subtype];
    case "mleader":
      return SUBCLASS_MLEADER;
    case "leader":
      return SUBCLASS_LEADER;
    case "tolerance":
      return SUBCLASS_FCF;
  }
}

/**
 * Input shape consumed by `serializeAnnotScaleContextRecord` — matches
 * the entries in `DxfObjectsSection.annotScaleContexts` exactly, with
 * `subclass` left optional for synthesised records.
 */
export type AnnotScaleContextRecordInput = {
  /** DXF group 5 — handle of the context-data record itself. */
  handle: string;
  /** DXF group 330 — handle of the owning extension dictionary. */
  ownerHandle: string;
  /** DXF group 340 — FK into ACAD_SCALELIST → AcDbScale. */
  scaleHandle: string;
  /**
   * Concrete subclass marker (e.g. "AcDbTextObjectContextData"). When
   * absent or empty, derived from `context.kind` (and `subtype` for
   * dimensions).
   */
  subclass?: string;
  /** Decoded per-scale fields. */
  context: AnnotScaleContext;
};

/**
 * Serialize one per-scale context-data record as DXF groups. Inverse
 * of the parser pipeline `dispatchObjectsAnnotativeRecord`.
 *
 * Emission shape (mirrors AutoCAD-authored output observed in
 * АБИ1.dxf — the QA fixture for annotation-scale parity):
 *
 * ```
 * 0   <SUBCLASS_TO_TYPE[subclass]>      ACDB_TEXTOBJECTCONTEXTDATA_CLASS, etc.
 * 5   <handle>
 * 330 <ownerHandle>
 * 100 AcDbObjectContextData             ← base subclass
 * 70  <flags>                           ← from context.flags
 * 100 AcDbAnnotScaleObjectContextData   ← intermediate base
 * 340 <scaleHandle>                     ← FK into ACAD_SCALELIST
 * <body groups per writeContextBody>
 * <rawGroups (round-trip)>
 * ```
 *
 * The concrete `AcDb<Kind>ObjectContextData` 100-marker is intentionally
 * OMITTED — AutoCAD's actual emission (per AcDbAnnotScaleObjectContextData
 * inheritance behaviour observed in the fixture) skips it, and the
 * parser's TYPE_TO_SUBCLASS fallback recovers the concrete kind from
 * group 0. Emitting the concrete marker would still parse correctly but
 * would diverge from byte-equivalent round-trip with AutoCAD-authored
 * input.
 */
export function serializeAnnotScaleContextRecord(
  rec: AnnotScaleContextRecordInput,
): DxfGroup[] {
  const out: DxfGroup[] = [];
  const subclass = resolveSubclass(rec.subclass, rec.context);
  const typeName = SUBCLASS_TO_TYPE[subclass] ?? subclass.toUpperCase();
  pushGroup(out, 0, typeName);
  if (rec.handle) pushGroup(out, 5, rec.handle);
  if (rec.ownerHandle) pushGroup(out, 330, rec.ownerHandle);
  pushGroup(out, 100, SUBCLASS_BASE);
  pushGroup(out, 70, rec.context.flags);
  // DXF group 290 — `AcDbObjectContextData` "is-default-context-data" bool
  // (Autodesk R2018). Emitted in the base subclass right after group 70 and
  // BEFORE the `AcDbAnnotScaleObjectContextData` marker (mirrors AutoCAD's
  // emission + the parser's envelope capture in dispatchObjectsAnnotativeRecord).
  // Optional: a source that never carried it (defaultFlag === undefined, e.g. the
  // synthetic QA fixture) stays byte-identical by not emitting it.
  if (rec.context.defaultFlag !== undefined) {
    pushGroup(out, 290, rec.context.defaultFlag);
  }
  pushGroup(out, 100, SUBCLASS_ANNOT_BASE);
  if (rec.scaleHandle) pushGroup(out, 340, rec.scaleHandle);
  writeContextBody(rec.context, out);
  return out;
}

// ── serializeAcadAnnotativeDecomposition ───────────────────────────────

/**
 * Serialize a `BlockRecord.annotativeDecompositions` map as the XData
 * group sequence AutoCAD writes under the `AcadAnnotativeDecomposition`
 * application name.
 *
 * Inverse of `parseAcadAnnotativeDecomposition`. Output shape:
 *
 * ```
 * 1001 AcadAnnotativeDecomposition
 * 1000 <scaleName>
 * 1005 <anonymousBlockHandle>
 * (repeats per entry)
 * ```
 *
 * Empty input returns an empty array — caller suppresses XData
 * emission entirely in that case (same convention as the BLOCK_RECORD
 * writer's `xdata`/`isAnnotative` branches in `writer.ts`).
 *
 * Iteration order follows insertion order of the `Record` (V8 guarantees
 * insertion order for string keys), which preserves the order the parser
 * built the map from the on-disk file.
 */
export function serializeAcadAnnotativeDecomposition(
  decompositions: Record<string, string>,
): DxfGroup[] {
  const entries = Object.entries(decompositions);
  if (entries.length === 0) return [];
  const out: DxfGroup[] = [];
  pushGroup(out, 1001, "AcadAnnotativeDecomposition");
  for (const [scaleName, handle] of entries) {
    pushGroup(out, 1000, scaleName);
    pushGroup(out, 1005, handle);
  }
  return out;
}
